/* eslint-disable react/prop-types */
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { Badge, Col, Nav, NavItem, NavLink, Row } from "reactstrap";
import Breadcrumbs from "../../../../components/Common/Breadcrumb";
import { INVITE_FLOW_STEP_LABELS } from "../inviteFlowStep";

const STEPS = [
  { id: 1, label: INVITE_FLOW_STEP_LABELS[1] },
  { id: 2, label: INVITE_FLOW_STEP_LABELS[2] },
  { id: 3, label: INVITE_FLOW_STEP_LABELS[3] },
];

function earliestLatestShiftDates(shiftPattern) {
  if (!Array.isArray(shiftPattern)) return { earliest: null, latest: null };
  let earliest = null;
  let latest = null;
  for (const p of shiftPattern) {
    const dates = p?.dates;
    if (!Array.isArray(dates)) continue;
    for (const d of dates) {
      const raw = typeof d === "string" ? d : d?.date;
      if (!raw) continue;
      const parsed = dayjs(raw);
      if (!parsed.isValid()) continue;
      if (!earliest || parsed.isBefore(earliest)) earliest = parsed;
      if (!latest || parsed.isAfter(latest)) latest = parsed;
    }
  }
  return { earliest, latest };
}

export default function InviteFlowLayout({
  booking,
  bookingSlug,
  activeStep = 1,
  children,
  hideSteps = false,
  breadcrumbTitle = "Invite workers",
  breadcrumbItem: breadcrumbItemProp,
}) {
  const site = booking?.location?.siteName ?? booking?.location?.searchAddress ?? "—";
  const { earliest, latest } = earliestLatestShiftDates(booking?.shiftPattern);
  const dateRange =
    earliest && latest
      ? earliest.isSame(latest, "day")
        ? earliest.format("D MMM YYYY")
        : `${earliest.format("D MMM")} – ${latest.format("D MMM YYYY")}`
      : "—";

  const breadcrumbItem =
    breadcrumbItemProp ??
    `Step ${activeStep}: ${INVITE_FLOW_STEP_LABELS[activeStep] ?? "Invite workers"}`;

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title={breadcrumbTitle} breadcrumbItem={breadcrumbItem} />

        <div className="mb-3">
          <Link to="/client/bookings" className="btn btn-link text-body px-0">
            <i className="mdi mdi-arrow-left me-1" />
            Back to bookings
          </Link>
          <Link to={`/client/bookings/${bookingSlug}`} className="btn btn-link text-primary ms-2">
            Booking details
          </Link>
        </div>

        <div className="border rounded p-3 mb-4 bg-light bg-opacity-50">
          <Row className="align-items-center g-2">
            <Col md="auto">
              <Badge color="primary" className="fs-6">
                {bookingSlug || "—"}
              </Badge>
            </Col>
            <Col md>
              <div className="fw-semibold">{site}</div>
              <div className="text-muted small">{dateRange}</div>
            </Col>
          </Row>
        </div>

        {!hideSteps ? (
          <Nav className="nav-pills nav-justified flex-column flex-sm-row mb-4 gap-2">
            {STEPS.map((s) => {
              const isFuture = s.id > activeStep;
              const isDone = s.id < activeStep;
              return (
                <NavItem key={s.id}>
                  <NavLink
                    disabled={isFuture}
                    active={s.id === activeStep}
                    className={s.id === activeStep ? "active" : ""}
                    style={{ cursor: isFuture ? "not-allowed" : "default" }}
                  >
                    <span className="d-flex align-items-center justify-content-center gap-1 flex-wrap">
                      {isDone && <i className="mdi mdi-check-circle text-success" aria-hidden />}
                      <span>
                        Step {s.id}: {s.label}
                      </span>
                    </span>
                    {isFuture && (
                      <span className="d-block small text-muted mt-1">Not available yet</span>
                    )}
                  </NavLink>
                </NavItem>
              );
            })}
          </Nav>
        ) : null}

        {children}
      </div>
    </div>
  );
}
